import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spin, Divider, Image, Typography, Input, DatePicker, Select, Popconfirm, message, Tag, Empty } from 'antd';
import { CalendarOutlined, FileTextOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CameraOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getExpenseDetailsApi,
  changeExpensePaymentStatusApi,
  updateExpenseImageApi,
  updateExpenseDateNoteApi,
  deleteExpenseApi,
  changeLabourTransactionStatusApi,
  addLabourTransactionApi,
  updateLabourTransactionApi,
  deleteLabourTransactionApi,
  getKharchoPaymentLogsApi,
  addKharchoPaymentLogApi,
  deleteKharchoPaymentLogApi,
  addKharchoPaymentLogImageApi,
} from '../../api/expenses';
import { getAccountsApi } from '../../api/accounts';
import { useNavigate, useParams } from 'react-router-dom';
import { formatCurrency, formatDate, getPaymentStatusColor } from '../../utils/formatters';
import { EXPENSE_PHOTO, PAYMENT_LOG_IMAGE } from '../../utils/constants';

const { Text } = Typography;

const PAY_OPTIONS = [
  { value: 'paid',   label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
];

function StatusTag({ isPaid }) {
  return (
    <Tag color={isPaid ? 'success' : 'error'} style={{ margin: 0, fontWeight: 600 }}>
      {isPaid ? 'Paid' : 'Unpaid'}
    </Tag>
  );
}

function PayButton({ isPaid, loading, onClick }) {
  return (
    <Button
      size="small"
      loading={loading}
      onClick={onClick}
      style={{
        borderColor: isPaid ? '#ff4d4f' : '#52c41a',
        color:       isPaid ? '#ff4d4f' : '#52c41a',
        fontWeight: 600,
        minWidth: 90,
        fontSize: 12,
      }}
    >
      {isPaid ? 'Mark Unpaid' : 'Mark Paid'}
    </Button>
  );
}

export default function ExpenseDetailModal({ open, onClose, expense, cropId, onRefresh }) {
  const navigate = useNavigate();
  const { farmId } = useParams();
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);

  // inline date/note edit
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaDate, setMetaDate]       = useState('');
  const [metaNote, setMetaNote]       = useState('');
  const [savingMeta, setSavingMeta]   = useState(false);

  // photo
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // main status toggle
  const [togglingMain, setTogglingMain] = useState(false);

  // labour state
  const [labourAccounts, setLabourAccounts]   = useState([]);
  const [showAddLabour, setShowAddLabour]     = useState(false);
  const [addForm, setAddForm]                 = useState(emptyAdd());
  const [savingAdd, setSavingAdd]             = useState(false);
  const [togglingLabour, setTogglingLabour]   = useState(null);
  const [editingLabour, setEditingLabour]     = useState(null);
  const [editLabourForm, setEditLabourForm]   = useState({});
  const [savingEditLabour, setSavingEditLabour] = useState(false);
  const [deletingLabour, setDeletingLabour]   = useState(null);

  const handleFullEdit = () => {
    onClose();
    navigate(`/farms/${farmId}/farm/${cropId}/add-expense`, { state: { editData: exp } });
  };

  // payment logs (shop_keeper)
  const [paymentLogs, setPaymentLogs]         = useState([]);
  const [logsSummary, setLogsSummary]         = useState(null);
  const [logsAccount, setLogsAccount]         = useState(null);
  const [loadingLogs, setLoadingLogs]         = useState(false);
  const [showAddLog, setShowAddLog]           = useState(false);
  const [addLogForm, setAddLogForm]           = useState({ amount: '', payment_date: '', note: '' });
  const [addLogPhoto, setAddLogPhoto]         = useState(null);
  const [savingAddLog, setSavingAddLog]       = useState(false);
  const [deletingLog, setDeletingLog]         = useState(null);
  const [uploadingLogImage, setUploadingLogImage] = useState(null);

  const exp      = data?.expense;
  const labours  = data?.labour_accounts || [];
  const isLabour = exp?.account_link === 'labour';
  const isShop   = exp?.account_link === 'shop_keeper';

  function emptyAdd() {
    return { account_id: null, amount: '', paymentStatus: 'paid', date: '', note: '' };
  }

  const fetchDetails = () => {
    if (!expense?.id) return;
    setLoading(true);
    getExpenseDetailsApi({ kharch_id: expense.id })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setData(null);
    setShowAddLabour(false);
    setEditingMeta(false);
    setPaymentLogs([]);
    setLogsSummary(null);
    setLogsAccount(null);
    setShowAddLog(false);
    setAddLogForm({ amount: '', payment_date: '', note: '' });
    setAddLogPhoto(null);
    fetchDetails();
  }, [open, expense?.id]);

  useEffect(() => {
    if (data?.expense?.account_link === 'labour' && labourAccounts.length === 0) {
      getAccountsApi()
        .then((res) => setLabourAccounts((res.data || []).filter((a) => a.type === 'labour')))
        .catch(() => {});
    }
  }, [data]);

  // ── inline meta edit ──────────────────────────────────
  const startEditMeta = () => {
    setMetaDate(exp?.expense_date || '');
    setMetaNote(exp?.details || '');
    setEditingMeta(true);
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await updateExpenseDateNoteApi({ id: exp.id, date: metaDate, details: metaNote });
      setData((d) => ({ ...d, expense: { ...d.expense, expense_date: metaDate, details: metaNote } }));
      setEditingMeta(false);
      onRefresh?.();
    } catch {
      message.error('Failed to save');
    } finally {
      setSavingMeta(false);
    }
  };

  // ── photo upload ──────────────────────────────────────
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('id',    exp.id);
      fd.append('photo', file);
      await updateExpenseImageApi(fd);
      fetchDetails();
      onRefresh?.();
    } catch {
      message.error('Photo upload failed');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // ── main payment toggle ───────────────────────────────
  const toggleMainStatus = async () => {
    const next = exp.payment_status === 'paid' ? 'unpaid' : 'paid';
    setTogglingMain(true);
    try {
      await changeExpensePaymentStatusApi({ id: exp.id, paymentStatus: next });
      setData((d) => ({ ...d, expense: { ...d.expense, payment_status: next } }));
      onRefresh?.();
    } catch {} finally {
      setTogglingMain(false);
    }
  };

  // ── delete expense ────────────────────────────────────
  const handleDelete = async () => {
    try {
      await deleteExpenseApi({ id: exp.id, cropId });
      message.success('Expense deleted');
      onRefresh?.();
      onClose();
    } catch {
      message.error('Failed to delete');
    }
  };

  // ── labour: toggle status ─────────────────────────────
  const toggleLabourStatus = async (item) => {
    const isPaid = item.credit_at !== null;
    const next   = isPaid ? 'unpaid' : 'paid';
    setTogglingLabour(item.transaction_id);
    try {
      await changeLabourTransactionStatusApi({ transaction_id: item.transaction_id, status: next });
      setData((d) => ({
        ...d,
        labour_accounts: d.labour_accounts.map((l) =>
          l.transaction_id === item.transaction_id
            ? { ...l, credit_at: next === 'paid' ? new Date().toISOString().split('T')[0] : null }
            : l
        ),
      }));
      onRefresh?.();
    } catch {} finally {
      setTogglingLabour(null);
    }
  };

  // ── labour: add ───────────────────────────────────────
  const handleAddLabour = async () => {
    if (!addForm.account_id || !addForm.amount) {
      message.warning('Select account and enter amount');
      return;
    }
    setSavingAdd(true);
    try {
      await addLabourTransactionApi({
        kharch_id:     exp.id,
        account_id:    addForm.account_id,
        amount:        addForm.amount,
        paymentStatus: addForm.paymentStatus,
        date:          addForm.date || exp.expense_date,
        note:          addForm.note,
      });
      setShowAddLabour(false);
      setAddForm(emptyAdd());
      fetchDetails();
      onRefresh?.();
    } catch {
      message.error('Failed to add');
    } finally {
      setSavingAdd(false);
    }
  };

  // ── labour: edit ──────────────────────────────────────
  const startEditLabour = (item) => {
    setEditingLabour(item.transaction_id);
    setEditLabourForm({ amount: item.amount, type: item.type, date: item.date || '' });
  };

  const saveEditLabour = async () => {
    setSavingEditLabour(true);
    try {
      await updateLabourTransactionApi({ id: editingLabour, ...editLabourForm });
      setEditingLabour(null);
      fetchDetails();
      onRefresh?.();
    } catch {
      message.error('Failed to update');
    } finally {
      setSavingEditLabour(false);
    }
  };

  // ── labour: delete ────────────────────────────────────
  const handleDeleteLabour = async (transactionId) => {
    setDeletingLabour(transactionId);
    try {
      await deleteLabourTransactionApi({ id: transactionId });
      setData((d) => ({ ...d, labour_accounts: d.labour_accounts.filter((l) => l.transaction_id !== transactionId) }));
      fetchDetails();
      onRefresh?.();
    } catch {
      message.error('Failed to delete');
    } finally {
      setDeletingLabour(null);
    }
  };

  // ── payment logs ──────────────────────────────────────
  const fetchPaymentLogs = (expenseId) => {
    setLoadingLogs(true);
    getKharchoPaymentLogsApi({ kharcho_id: expenseId })
      .then((res) => {
        setPaymentLogs(res.data?.logs    || []);
        setLogsSummary(res.data?.summary || null);
        setLogsAccount(res.data?.account || null);
      })
      .catch(() => {})
      .finally(() => setLoadingLogs(false));
  };

  useEffect(() => {
    if (isShop && exp?.id) fetchPaymentLogs(exp.id);
  }, [isShop, exp?.id]);

  const handleAddLog = async () => {
    if (!addLogForm.amount || !addLogForm.payment_date) {
      message.warning('Amount and date are required');
      return;
    }
    const remaining = Number(logsSummary?.remaining_amount) || 0;
    if (Number(addLogForm.amount) > remaining) {
      message.warning(`Amount cannot exceed remaining ₹${remaining.toLocaleString()}`);
      return;
    }
    setSavingAddLog(true);
    try {
      const fd = new FormData();
      fd.append('kharcho_id',    exp.id);
      fd.append('amount',        addLogForm.amount);
      fd.append('payment_date',  addLogForm.payment_date);
      if (addLogForm.note)  fd.append('note',          addLogForm.note);
      if (addLogPhoto)      fd.append('payment_image', addLogPhoto);
      await addKharchoPaymentLogApi(fd);
      setShowAddLog(false);
      setAddLogForm({ amount: '', payment_date: '', note: '' });
      setAddLogPhoto(null);
      fetchPaymentLogs(exp.id);
    } catch {
      message.error('Failed to add payment log');
    } finally {
      setSavingAddLog(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    setDeletingLog(logId);
    try {
      await deleteKharchoPaymentLogApi({ payment_log_id: logId });
      fetchPaymentLogs(exp.id);
    } catch {
      message.error('Failed to delete');
    } finally {
      setDeletingLog(null);
    }
  };

  const handleLogImageUpload = async (logId, file) => {
    setUploadingLogImage(logId);
    try {
      const fd = new FormData();
      fd.append('payment_log_id', logId);
      fd.append('payment_image',  file);
      await addKharchoPaymentLogImageApi(fd);
      fetchPaymentLogs(exp.id);
    } catch {
      message.error('Image upload failed');
    } finally {
      setUploadingLogImage(null);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={600}
        destroyOnClose
        styles={{ body: { padding: '16px 24px 20px' } }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 700 }}>{expense?.kharcho}</span>
            {expense?.account_link && expense.account_link !== '' && (
              <Tag color={expense.account_link === 'labour' ? 'blue' : 'purple'} style={{ margin: 0 }}>
                {expense.account_link === 'labour' ? 'Labour' : 'Vendor'}
              </Tag>
            )}
          </div>
        }
      >
        {loading && !data ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : exp ? (
          <div>
            {/* ── Amount + action buttons ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: '#fa8c16', lineHeight: 1 }}>
                {formatCurrency(exp.amount)}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {!isLabour && !isShop && (
                  <>
                    <StatusTag isPaid={exp.payment_status === 'paid'} />
                    <PayButton
                      isPaid={exp.payment_status === 'paid'}
                      loading={togglingMain}
                      onClick={toggleMainStatus}
                    />
                  </>
                )}
                <Button size="small" icon={<EditOutlined />} onClick={handleFullEdit} />
                <Popconfirm
                  title={isLabour
                    ? 'This will also delete all linked labour transactions. Continue?'
                    : 'Delete this expense?'}
                  onConfirm={handleDelete}
                  okButtonProps={{ danger: true }}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </div>

            {/* ── Date + Note (inline editable) ── */}
            {editingMeta ? (
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 12, marginBottom: 12, background: '#fafafa' }}>
                <DatePicker
                  style={{ width: '100%', marginBottom: 8 }}
                  value={metaDate ? dayjs(metaDate) : null}
                  onChange={(d) => setMetaDate(d ? d.format('YYYY-MM-DD') : '')}
                />
                <Input.TextArea
                  rows={2} placeholder="Note..."
                  value={metaNote}
                  onChange={(e) => setMetaNote(e.target.value)}
                  style={{ marginBottom: 8 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingMeta(false)}>Cancel</Button>
                  <Button size="small" type="primary" icon={<CheckOutlined />} loading={savingMeta} onClick={saveMeta}
                    style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>Save</Button>
                </div>
              </div>
            ) : (
              <div
                style={{ marginBottom: 12, border: '1px solid #f0f0f0', borderRadius: 8, padding: '10px 12px', background: '#fafafa', cursor: 'pointer', position: 'relative' }}
                onClick={startEditMeta}
                title="Edit date & note"
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    <CalendarOutlined style={{ marginRight: 5 }} />
                    {formatDate(exp.expense_date)}
                  </Text>
                  <EditOutlined style={{ color: '#1a6b2a', fontSize: 13 }} />
                </div>
                {exp.details ? (
                  <div style={{ fontSize: 13, color: '#555', marginTop: 6 }}>
                    <FileTextOutlined style={{ marginRight: 6, color: '#ccc' }} />
                    {exp.details}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#bbb', marginTop: 4 }}>
                    <FileTextOutlined style={{ marginRight: 5 }} />
                    Add note...
                  </div>
                )}
              </div>
            )}

            {/* ── Photo ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              {(exp.photo_url || exp.photo) && (
                <Image
                  src={exp.photo_url || EXPENSE_PHOTO(exp.photo)}
                  width={64} height={64}
                  style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }}
                />
              )}
              {exp.receipt_photo && (
                <div>
                  <Image
                    src={PAYMENT_LOG_IMAGE(exp.receipt_photo)}
                    width={64} height={64}
                    style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }}
                  />
                  <div style={{ fontSize: 10, color: '#888', textAlign: 'center', marginTop: 2 }}>Receipt</div>
                </div>
              )}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 6, border: '1px solid #d9d9d9', cursor: 'pointer', background: '#fafafa', fontSize: 13 }}>
                {uploadingPhoto ? <Spin size="small" /> : <CameraOutlined />}
                <span>{exp.photo ? 'Change Photo' : 'Add Photo'}</span>
                <input type="file" accept="image/*" hidden ref={photoInputRef} onChange={handlePhotoChange} />
              </label>
            </div>

            {/* ── Labour accounts ── */}
            {isLabour && (
              <>
                <Divider style={{ margin: '14px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <Text strong>Labour Accounts</Text>
                  <Button size="small" icon={<PlusOutlined />}
                    style={{ borderColor: '#1a6b2a', color: '#1a6b2a' }}
                    onClick={() => setShowAddLabour((v) => !v)}>
                    Add Labour
                  </Button>
                </div>

                {/* add form */}
                {showAddLabour && (
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 12, marginBottom: 12, background: '#fafafa' }}>
                    <Select
                      showSearch placeholder="Select labour account"
                      style={{ width: '100%', marginBottom: 8 }}
                      value={addForm.account_id}
                      onChange={(v) => setAddForm((f) => ({ ...f, account_id: v }))}
                      filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
                      options={labourAccounts
                        .filter((a) => !labours.some((l) => l.account_id === a.id))
                        .map((a) => ({ value: a.id, label: a.name }))}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <Input
                        type="number" min={0} prefix="₹" placeholder="Amount"
                        value={addForm.amount}
                        onChange={(e) => setAddForm((f) => ({ ...f, amount: e.target.value }))}
                      />
                      <Select
                        value={addForm.paymentStatus}
                        onChange={(v) => setAddForm((f) => ({ ...f, paymentStatus: v }))}
                        options={PAY_OPTIONS}
                      />
                    </div>
                    <DatePicker
                      style={{ width: '100%', marginBottom: 8 }}
                      value={addForm.date ? dayjs(addForm.date) : null}
                      onChange={(d) => setAddForm((f) => ({ ...f, date: d ? d.format('YYYY-MM-DD') : '' }))}
                      placeholder="Date (defaults to expense date)"
                    />
                    <Input
                      placeholder="Note (optional)"
                      value={addForm.note}
                      onChange={(e) => setAddForm((f) => ({ ...f, note: e.target.value }))}
                      style={{ marginBottom: 8 }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="small" onClick={() => { setShowAddLabour(false); setAddForm(emptyAdd()); }}>Cancel</Button>
                      <Button size="small" type="primary" loading={savingAdd} onClick={handleAddLabour}
                        style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>Add</Button>
                    </div>
                  </div>
                )}

                {/* labour list */}
                {labours.length === 0 ? (
                  <div style={{ color: '#bfbfbf', textAlign: 'center', padding: '12px 0', fontSize: 13 }}>
                    No labour accounts linked
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {labours.map((item) => {
                      const isPaid   = item.credit_at !== null;
                      const isEditingThis = editingLabour === item.transaction_id;

                      return (
                        <div key={item.transaction_id}
                          style={{
                            borderRadius: 10, overflow: 'hidden', background: '#fff',
                            border: '1px solid #f0f0f0',
                            borderLeft: `4px solid ${isPaid ? '#52c41a' : '#ff4d4f'}`,
                          }}>
                          {isEditingThis ? (
                            <div style={{ padding: '12px 14px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                <Input
                                  type="number" min={0} prefix="₹" placeholder="Amount"
                                  value={editLabourForm.amount}
                                  onChange={(e) => setEditLabourForm((f) => ({ ...f, amount: e.target.value }))}
                                />
                                <Select
                                  value={editLabourForm.type}
                                  onChange={(v) => setEditLabourForm((f) => ({ ...f, type: v }))}
                                  options={[
                                    { value: 'credit', label: 'Paid (Credit)' },
                                    { value: 'debit',  label: 'Unpaid (Debit)' },
                                  ]}
                                />
                              </div>
                              <DatePicker
                                style={{ width: '100%', marginBottom: 8 }}
                                value={editLabourForm.date ? dayjs(editLabourForm.date) : null}
                                onChange={(d) => setEditLabourForm((f) => ({ ...f, date: d ? d.format('YYYY-MM-DD') : '' }))}
                              />
                              <div style={{ display: 'flex', gap: 8 }}>
                                <Button size="small" onClick={() => setEditingLabour(null)}>Cancel</Button>
                                <Button size="small" type="primary" loading={savingEditLabour} onClick={saveEditLabour}
                                  style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>Save</Button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                              {/* avatar */}
                              <div style={{
                                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                                background: isPaid ? '#f6ffed' : '#fff1f0',
                                border: `1px solid ${isPaid ? '#b7eb8f' : '#ffa39e'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 16, fontWeight: 700, color: isPaid ? '#52c41a' : '#ff4d4f',
                              }}>
                                {item.account_name?.charAt(0)?.toUpperCase()}
                              </div>

                              {/* info */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                  <span style={{ fontWeight: 600, fontSize: 14 }}>{item.account_name}</span>
                                  <StatusTag isPaid={isPaid} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <span style={{ color: '#fa8c16', fontWeight: 700, fontSize: 14 }}>{formatCurrency(item.amount)}</span>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    <CalendarOutlined style={{ marginRight: 3 }} />{formatDate(item.date)}
                                  </Text>
                                </div>
                                {item.account_mobile && (
                                  <Text type="secondary" style={{ fontSize: 11 }}>{item.account_mobile}</Text>
                                )}
                              </div>

                              {/* actions */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                                <PayButton
                                  isPaid={isPaid}
                                  loading={togglingLabour === item.transaction_id}
                                  onClick={() => toggleLabourStatus(item)}
                                />
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <Button size="small" icon={<EditOutlined />} onClick={() => startEditLabour(item)} />
                                  <Popconfirm title="Delete this labour transaction?" onConfirm={() => handleDeleteLabour(item.transaction_id)} okButtonProps={{ danger: true }}>
                                    <Button size="small" danger icon={<DeleteOutlined />} loading={deletingLabour === item.transaction_id} />
                                  </Popconfirm>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ── Shop keeper account ── */}
            {isShop && data?.shop_keeper_account && (
              <>
                <Divider style={{ margin: '14px 0' }} />
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Store Account</Text>
                <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '10px 12px', fontWeight: 600 }}>
                  {data.shop_keeper_account.name}
                </div>
              </>
            )}

            {/* ── Payment Logs (shop_keeper only) ── */}
            {isShop && (
              <>
                <Divider style={{ margin: '14px 0' }} />

                {/* Summary box */}
                {logsSummary && (
                  <div style={{ background: '#fafafa', borderRadius: 10, padding: '4px 16px', marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                      <span style={{ color: '#888', fontSize: 13 }}>Total Amount</span>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{formatCurrency(Number(logsSummary.total_amount) || 0)}</span>
                    </div>
                    <Divider style={{ margin: '2px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                      <span style={{ color: '#888', fontSize: 13 }}>Total Paid</span>
                      <span style={{ fontWeight: 700, fontSize: 15, color: '#52c41a' }}>{formatCurrency(Number(logsSummary.received_amount) || 0)}</span>
                    </div>
                    <Divider style={{ margin: '2px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                      <span style={{ color: '#888', fontSize: 13 }}>Remaining</span>
                      <span style={{ fontWeight: 700, fontSize: 15, color: Number(logsSummary.remaining_amount) > 0 ? '#fa8c16' : '#52c41a' }}>
                        {formatCurrency(Number(logsSummary.remaining_amount) || 0)}
                      </span>
                    </div>
                    <Divider style={{ margin: '2px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                      <span style={{ color: '#888', fontSize: 13 }}>Status</span>
                      <Tag color={getPaymentStatusColor(logsSummary.payment_status)} style={{ margin: 0 }}>
                        {logsSummary.payment_status === 'paid' ? 'Paid' : logsSummary.payment_status === 'partial' ? 'Partial' : 'Unpaid'}
                      </Tag>
                    </div>
                  </div>
                )}

                {/* Logs header + add button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#555' }}>Payment Logs</span>
                  {!showAddLog && (
                    <Button size="small" type="primary" icon={<PlusOutlined />}
                      style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
                      onClick={() => setShowAddLog(true)}>
                      Add Payment
                    </Button>
                  )}
                </div>

                {/* Inline add form */}
                {showAddLog && (
                  <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    {/* Row 1: Amount + Date */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <div>
                        <Input
                          type="number" placeholder="Amount (₹)"
                          value={addLogForm.amount}
                          max={Number(logsSummary?.remaining_amount) || undefined}
                          status={addLogForm.amount && Number(addLogForm.amount) > (Number(logsSummary?.remaining_amount) || 0) ? 'error' : ''}
                          onChange={(e) => setAddLogForm((f) => ({ ...f, amount: e.target.value }))}
                        />
                        {logsSummary?.remaining_amount > 0 && (
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                            Max: {formatCurrency(Number(logsSummary.remaining_amount))}
                          </div>
                        )}
                      </div>
                      <DatePicker
                        value={addLogForm.payment_date ? dayjs(addLogForm.payment_date) : null}
                        onChange={(d) => setAddLogForm((f) => ({ ...f, payment_date: d ? d.format('YYYY-MM-DD') : '' }))}
                        style={{ width: '100%', height: 32 }}
                      />
                    </div>
                    {/* Row 2: Remark + Photo */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <Input
                        placeholder="Remark (optional)"
                        value={addLogForm.note}
                        onChange={(e) => setAddLogForm((f) => ({ ...f, note: e.target.value }))}
                        style={{ flex: 1 }}
                      />
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 6, border: '1px solid #d9d9d9', cursor: 'pointer', background: '#fff', fontSize: 13, flexShrink: 0, height: 32 }}>
                        <CameraOutlined />
                        <span>{addLogPhoto ? addLogPhoto.name.slice(0, 10) + '…' : 'Photo'}</span>
                        <input type="file" accept="image/*" hidden onChange={(e) => { setAddLogPhoto(e.target.files?.[0] || null); e.target.value = ''; }} />
                      </label>
                    </div>
                    {/* Row 3: Cancel + Save */}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => { setShowAddLog(false); setAddLogForm({ amount: '', payment_date: '', note: '' }); setAddLogPhoto(null); }}>Cancel</Button>
                      <Button size="small" type="primary" loading={savingAddLog} onClick={handleAddLog}
                        style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>Save</Button>
                    </div>
                  </div>
                )}

                {/* Logs list */}
                {loadingLogs ? (
                  <div style={{ textAlign: 'center', padding: '12px 0' }}><Spin size="small" /></div>
                ) : paymentLogs.length === 0 ? (
                  <Empty description="No payment logs" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {paymentLogs.map((log) => (
                      <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '10px 14px' }}>
                        {/* amount + date + note */}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 15, color: '#52c41a' }}>{formatCurrency(log.amount)}</div>
                          <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{formatDate(log.credit_at || log.payment_date)}</div>
                          {log.note && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{log.note}</div>}
                        </div>

                        {/* image / upload */}
                        {log.image ? (
                          <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
                            <Image
                              src={log.image_url || PAYMENT_LOG_IMAGE(log.image)}
                              width={48} height={48}
                              style={{ objectFit: 'cover', borderRadius: 6, display: 'block' }}
                            />
                            <label
                              htmlFor={`klog-img-${log.id}`}
                              onClick={(e) => e.stopPropagation()}
                              style={{ position: 'absolute', bottom: 2, right: 2, width: 18, height: 18, background: 'rgba(0,0,0,0.7)', borderRadius: 3, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <CameraOutlined style={{ color: '#fff', fontSize: 10 }} />
                            </label>
                            <input id={`klog-img-${log.id}`} type="file" accept="image/*" style={{ display: 'none' }}
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogImageUpload(log.id, f); e.target.value = ''; }} />
                          </div>
                        ) : (
                          <label style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 6, border: '1px dashed #d9d9d9', cursor: 'pointer', background: '#fafafa', flexShrink: 0 }}>
                            {uploadingLogImage === log.id ? <Spin size="small" /> : <CameraOutlined style={{ color: '#bbb', fontSize: 16 }} />}
                            <input type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogImageUpload(log.id, f); e.target.value = ''; }} />
                          </label>
                        )}

                        {/* delete */}
                        <Popconfirm title="Delete this payment log?" onConfirm={() => handleDeleteLog(log.id)} okButtonProps={{ danger: true }}>
                          <Button size="small" danger icon={<DeleteOutlined />} loading={deletingLog === log.id} />
                        </Popconfirm>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── No account link: show payment status ── */}
            {!isLabour && !isShop && (
              <>
                <Divider style={{ margin: '14px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text strong>Payment Status</Text>
                    <StatusTag isPaid={exp.payment_status === 'paid'} />
                  </div>
                  <PayButton
                    isPaid={exp.payment_status === 'paid'}
                    loading={togglingMain}
                    onClick={toggleMainStatus}
                  />
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 48, color: '#aaa' }}>No data</div>
        )}
      </Modal>

    </>
  );
}
